import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { getChangeRequest, updateChangeRequest } from '../../services/changeRequestService';

const ChangeRequestEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    change_type: 'Scope',
    priority: 'Medium',
    justification: '',
    benefits: '',
    impact_scope: '',
    impact_schedule: '',
    impact_budget: '',
    impact_level: 'Low',
    implementation_plan: '',
    target_completion_date: ''
  });

  useEffect(() => {
    fetchChangeRequest();
  }, [id]);

  const fetchChangeRequest = async () => {
    try {
      setLoading(true);
      const response = await getChangeRequest(id);
      const cr = response.data;
      setFormData({
        title: cr.title || '',
        description: cr.description || '',
        change_type: cr.change_type || 'Scope',
        priority: cr.priority || 'Medium',
        justification: cr.justification || '',
        benefits: cr.benefits || '',
        impact_scope: cr.impact_scope || '',
        impact_schedule: cr.impact_schedule || '',
        impact_budget: cr.impact_budget || '',
        impact_level: cr.impact_level || 'Low',
        implementation_plan: cr.implementation_plan || '',
        target_completion_date: cr.target_completion_date ? cr.target_completion_date.split('T')[0] : ''
      });
    } catch (error) {
      console.error('Error fetching change request:', error);
      toast.error('Failed to load change request');
      navigate('/change-requests');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateChangeRequest(id, formData);
      toast.success('Change request updated successfully');
      navigate(`/change-requests/${id}`);
    } catch (error) {
      console.error('Error updating change request:', error);
      toast.error(error.response?.data?.message || 'Failed to update change request');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/app/change-requests/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" /> Back to Change Request
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Change Request</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Change Type</label>
              <select
                name="change_type"
                value={formData.change_type}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Scope">Scope</option>
                <option value="Schedule">Schedule</option>
                <option value="Budget">Budget</option>
                <option value="Resource">Resource</option>
                <option value="Quality">Quality</option>
                <option value="Risk">Risk</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
              <textarea
                name="justification"
                value={formData.justification}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
              <textarea
                name="benefits"
                value={formData.benefits}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate(`/app/change-requests/${id}`)}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangeRequestEdit;


